import React, { useState, useRef } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { TextArea } from '../ui/TextArea';
import { Alert } from '../ui/Alert';
import { leadService } from '../../services/leadService';
import { supabase } from '../../lib/supabase';
import { ContactPhotoCapture } from './ContactPhotoCapture';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const ExchangeContactModal = ({ card, sourceContext, onClose, onSaveOwnerContact, onTrack }) => {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const trackedSuccess = useRef(false);
  
  const [photoFile, setPhotoFile] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    message: '',
    consent: false,
    botField: ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.botField) {
      setStep('success');
      return;
    }

    if (!formData.consent) {
      setError('You must agree to share your details to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let contactPhotoUrl = null;

      // Handle optional photo upload
      if (photoFile) {
        try {
          const ext = photoFile.name.split('.').pop() || 'jpg';
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
          
          const { error: uploadError } = await supabase.storage
            .from('contact_photos')
            .upload(`inbound/${fileName}`, photoFile);
            
          if (!uploadError) {
            const { data } = supabase.storage
              .from('contact_photos')
              .getPublicUrl(`inbound/${fileName}`);
            contactPhotoUrl = data.publicUrl;
          } else {
            console.warn("Photo upload failed, proceeding without photo.", uploadError);
          }
        } catch (err) {
          console.warn("Photo upload failed, proceeding without photo.", err);
        }
      }

      await leadService.submitLead(card.public_slug, {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        jobTitle: formData.jobTitle,
        message: formData.message,
        consent: formData.consent,
        source: sourceContext.source || 'public_card',
        qrToken: sourceContext.token || null,
        contactPhotoUrl
      });

      if (onTrack && !trackedSuccess.current) {
        trackedSuccess.current = true;
        onTrack('connection_completed');
      }

      setStep('success');
    } catch (err) {
      setError(err.message || 'We couldn’t share your details right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm sm:items-center items-end">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg shadow-2xl animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-slate-900 text-lg">Share Your Contact</h3>
          {step === 'form' && (
            <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors p-1">
              <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
            </button>
          )}
        </div>

        <div className="p-6 overflow-y-auto hide-scrollbar">
          {step === 'form' ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="text-slate-600 mb-6">
                Share your details with <strong>{card.full_name}</strong>.
              </p>

              {error && <Alert variant="error" message={error} className="mb-4" />}

              <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <input type="text" name="botField" tabIndex="-1" autoComplete="off" value={formData.botField} onChange={handleChange} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input label={<>Full Name <span className="text-red-500">*</span></>} name="fullName" value={formData.fullName} onChange={handleChange} required maxLength={100} />
                <Input label={<>Email <span className="text-red-500">*</span></>} type="email" name="email" value={formData.email} onChange={handleChange} required maxLength={254} />
                <Input label={<>Phone <span className="text-red-500">*</span></>} type="tel" name="phone" value={formData.phone} onChange={handleChange} required maxLength={40} />
                <Input label="Company (Optional)" name="company" value={formData.company} onChange={handleChange} maxLength={120} />
                <Input label="Job Title (Optional)" className="sm:col-span-2" name="jobTitle" value={formData.jobTitle} onChange={handleChange} maxLength={120} />
              </div>

              <ContactPhotoCapture file={photoFile} onFileChange={setPhotoFile} />

              <TextArea label="Message (Optional)" name="message" value={formData.message} onChange={handleChange} maxLength={500} rows={2} />

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mt-6">
                <label className="flex items-start cursor-pointer">
                  <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} className="mt-1 rounded border-slate-300 w-5 h-5 text-slate-900 focus:ring-slate-900 shrink-0" />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-slate-900 block mb-1">
                      I agree to share these details (and optional photo) with {card.full_name} so they can contact me.
                    </span>
                    <span className="text-xs text-slate-500 block">
                      Your details will be shared with this card owner and stored securely in Meetoi.
                    </span>
                  </div>
                </label>
              </div>

              <div className="pt-4 flex flex-col gap-3">
                <Button type="submit" isLoading={loading} size="lg" className="w-full">
                  Share Details
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-4xl" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Thanks!</h2>
              <p className="text-slate-600 mb-8 max-w-sm mx-auto">
                Your contact details have been shared with <strong>{card.full_name}</strong>.
              </p>
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 mb-8">
                <Button variant="primary" className="w-full mb-3" onClick={() => { onSaveOwnerContact(); onClose(); }}>
                  <SafeIcon icon={FiIcons.FiDownload} className="mr-2" /> Save {card.full_name}'s Contact
                </Button>
                <Button variant="ghost" className="w-full" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};